import React, { useEffect, useState } from 'react';

import ProjectTablesPanel from './ProjectTablesPanel';
import ToolHelpButton from '../../components/ToolHelpButton';

function ProjectsSection({
    activeProjectId,
    visibleWorkspaces,
    user,
    marketplaces,
    companyUsers,
    getWorkspaceRole,
    getUserLabel,
    getAccessRoleLabel,
    openProjectDetail,
    closeProjectDetail,
    openProjectModal,
    openMarketplaceProjectTools,
}) {
    const [activeProjectTool, setActiveProjectTool] = useState('project_tables');

    useEffect(() => {
        setActiveProjectTool('project_tables');
    }, [activeProjectId]);

    return (
        <div className="panel tasks projects">
            <div className="panel__head">
                <div>
                    <h3>Проекты</h3>
                    <p className="muted">Управляйте пространствами работы и привязкой кабинетов.</p>
                </div>
                {!activeProjectId && (
                    <button
                        type="button"
                        className="ghost"
                        onClick={() => openProjectModal()}
                    >
                        Создать проект
                    </button>
                )}
                {activeProjectId && (
                    <button type="button" className="ghost" onClick={closeProjectDetail}>
                        Назад к проектам
                    </button>
                )}
            </div>

            {visibleWorkspaces.length <= 1 && !activeProjectId && (
                <div className="empty">
                    У вас единый проект. Для разделения по проектам перейдите в настройки.
                </div>
            )}

            {!activeProjectId && (
                <div className="projects__grid">
                    {visibleWorkspaces.map((workspace) => {
                        const role = getWorkspaceRole(workspace);
                        const isOwner = workspace.owner_user_id === user?.id;
                        const accessLabel = getAccessRoleLabel(role, isOwner);

                        return (
                            <div key={workspace.id} className="card project-card">
                                <div className="card__head">
                                    <h4>{workspace.name}</h4>
                                    <div className="muted">
                                        {(workspace.cabinets || []).length} кабинетов · {(workspace.members || []).length} сотрудников
                                    </div>
                                </div>
                                <div className="muted">Доступ: {accessLabel || 'нет'}</div>
                                <p className="muted">{workspace.description || 'Описание не заполнено.'}</p>
                                <div className="card__actions">
                                    <button
                                        type="button"
                                        className="ghost"
                                        onClick={() => openProjectDetail(workspace.id)}
                                    >
                                        Открыть
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {activeProjectId && (() => {
                const workspace = visibleWorkspaces.find((item) => item.id === activeProjectId);
                if (!workspace) {
                    return <div className="empty">Проект не найден.</div>;
                }

                const memberNames = (workspace.members || [])
                    .map((member) => {
                        const userItem = companyUsers.find((u) => u.id === member.user_id);
                        return userItem ? getUserLabel(userItem) : null;
                    })
                    .filter(Boolean);

                const cabinetNames = (workspace.cabinets || [])
                    .map((cabinet) => {
                        const account = marketplaces.find((item) => item.id === cabinet.marketplace_account_id);
                        if (account?.account_name) return account.account_name;
                        return null;
                    })
                    .filter(Boolean);

                const role = getWorkspaceRole(workspace);
                const isOwner = workspace.owner_user_id === user?.id;
                const accessLabel = getAccessRoleLabel(role, isOwner);
                const canEditProjectAssortment = isOwner || role === 'admin' || role === 'admin_api';
                const canEditProject = canEditProjectAssortment;

                return (
                    <div className="projects__detail">
                        <div className="panel">
                            <div className="panel__head">
                                <div>
                                    <h3>{workspace.name}</h3>
                                    <p className="muted">{workspace.description || 'Описание не заполнено.'}</p>
                                </div>
                                {canEditProject && (
                                    <button
                                        type="button"
                                        className="primary project-edit"
                                        onClick={() => openProjectModal(workspace)}
                                    >
                                        Изменить
                                    </button>
                                )}
                            </div>

                            <div className="project-meta">
                                <div className="project-meta__item">
                                    <span className="muted">Доступ</span>
                                    <strong>{accessLabel || 'нет'}</strong>
                                </div>
                            </div>

                            <div className="project-meta">
                                <div className="project-meta__item">
                                    <span className="muted">Сотрудники</span>
                                    <div className="project-tags">
                                        {memberNames.length === 0
                                            ? <span className="muted">Сотрудники не выбраны.</span>
                                            : memberNames.map((name) => (
                                                <span key={name} className="chip">{name}</span>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            <div className="project-meta">
                                <div className="project-meta__item">
                                    <span className="muted">Кабинеты</span>
                                    <div className="project-tags">
                                        {cabinetNames.length === 0
                                            ? <span className="muted">Кабинеты не выбраны.</span>
                                            : cabinetNames.map((name) => (
                                                <span key={name} className="chip">{name}</span>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            <div className="project-functions">
                                <span className="muted">Функции проекта</span>
                                <div className="project-functions__buttons">
                                    <button
                                        type="button"
                                        className={activeProjectTool === 'project_tables' ? 'active' : 'ghost'}
                                        onClick={() => setActiveProjectTool('project_tables')}
                                    >
                                        Таблицы проекта
                                        <ToolHelpButton toolKey="project_tables" />
                                    </button>
                                    <button
                                        type="button"
                                        className="ghost"
                                        onClick={() => openMarketplaceProjectTools?.('ozon', workspace.id)}
                                    >
                                        Инструменты OZON
                                    </button>
                                    <button
                                        type="button"
                                        className="ghost"
                                        onClick={() => openMarketplaceProjectTools?.('wildberries', workspace.id)}
                                    >
                                        Инструменты WB
                                    </button>
                                </div>
                            </div>
                        </div>

                        {activeProjectTool === 'project_tables' && (
                            <div className="panel">
                                <ProjectTablesPanel
                                    workspaceId={workspace.id}
                                    canEdit={canEditProjectAssortment}
                                />
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}

export default ProjectsSection;
